/**
 * Zentraler Stock-Neuberechnungs-Pfad nach Lots-Bewegung (Achat, Transfer, Migration).
 * Ersetzt verteilte Aufrufe von recalculateAndUpdateStocksBatch + preCalculateStockData + Inventar-Hook.
 *
 * ADR-0013 Layout: öffentliche Owner-API = domain/inventory/recalculation (Adapter).
 */

import { getCurrentBusinessId } from '../../../utils/sharedCore';
import {
  publishInstantCacheUpdated,
  publishLotUpdated,
  publishLegacyWindowEvent
} from '../../../infrastructure/events/legacyEventBridge';
import { listLotsForBusiness } from '../../../app/di/container';
import { readStockSnapshotsSyncForBusiness } from '../snapshots';

export const STOCK_RECALCULATED_EVENT = 'stockRecalculated';

let coordinatedRecalcDepth = 0;

export function isCoordinatedStockRecalculationActive() {
  return coordinatedRecalcDepth > 0;
}

function normalizeEntries(entries = []) {
  const unique = [];
  const seen = new Set();
  for (const entry of entries) {
    if (!entry?.productId) continue;
    const location = entry.location ?? null;
    const key = `${entry.productId}|${location || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ productId: entry.productId, location });
  }
  return unique;
}

/**
 * @param {object} params
 * @param {string} [params.businessId]
 * @param {{ productId: string|number, location?: string|null }[]} [params.entries]
 * @param {Array} [params.lots]
 * @param {Array} [params.purchases]
 * @param {Array} [params.products]
 * @param {string[]} [params.pricingProductIds]
 * @param {string} [params.source]
 * @param {boolean} [params.showInventoryOverlay]
 * @param {boolean} [params.refreshLotsCache]
 * @param {boolean} [params.forceInstantCacheStockResync] — re-enrich products + clear qty keys (achat import)
 */
export async function runStockRecalculationAfterMovement({
  businessId = null,
  entries = [],
  lots = null,
  purchases = null,
  products = null,
  pricingProductIds = [],
  source = 'movement',
  showInventoryOverlay = false,
  refreshLotsCache = true,
  forceInstantCacheStockResync = false
} = {}) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: false });
  if (!bid) {
    return { success: false, error: 'no_business_id' };
  }

  const normalizedEntries = normalizeEntries(entries);
  coordinatedRecalcDepth += 1;

  try {
    if (refreshLotsCache) {
      // Refresh in place — do not clear first (empty-cache window under NO_KV_BLOB)
      const { refreshLotsReadCache } = await import('../../../utils/lotsReadSource');
      await refreshLotsReadCache(bid);
    }

    const { recalculateAndUpdateStocksBatch } = await import(
      '../../../services/stockCalculationService'
    );
    const batchResult = normalizedEntries.length
      ? await recalculateAndUpdateStocksBatch(normalizedEntries)
      : { success: true, results: [] };

    const lotsArray = lots ?? (await listLotsForBusiness(bid));

    const { readProductsFromBestSource } = await import('../../../utils/productsReadSource');
    const { readPurchasesFromBestSource } = await import('../../../utils/purchasesReadSource');
    const [productsList, purchasesList] = await Promise.all([
      products ?? readProductsFromBestSource(bid),
      purchases ?? readPurchasesFromBestSource(bid)
    ]);

    const { warmStockProjectionAfterMovement, afterStockMovementCommit, afterPurchaseCommit, invalidateForDomainEvent } =
      await import('../../../services/cacheReadModel');

    const lotsArr = Array.isArray(lotsArray) ? lotsArray : [];
    const purchasesArr = Array.isArray(purchasesList) ? purchasesList : [];
    const productsArr = Array.isArray(productsList) ? productsList : [];
    const forceResync =
      forceInstantCacheStockResync ||
      source === 'achat' ||
      source === 'achat_edit' ||
      source === 'repair';

    const pricingIds =
      pricingProductIds.length > 0
        ? pricingProductIds
        : [
            ...new Set(
              normalizedEntries.map((e) => e.productId).filter(Boolean)
            )
          ];

    const stockWarmed = warmStockProjectionAfterMovement({
      lots: lotsArr,
      products: productsArr,
      purchases: purchasesArr,
      pricingProductIds: pricingIds,
      forceResync,
      stockSnapshots: readStockSnapshotsSyncForBusiness(bid)
    });

    if (stockWarmed) {
      publishInstantCacheUpdated({ businessId: bid, source });

      try {
        if (
          source === 'purchase' ||
          source === 'achat' ||
          source === 'achat_edit' ||
          source === 'achat_delete' ||
          source === 'repair' ||
          source === 'receiveGoods'
        ) {
          afterPurchaseCommit();
        } else if (source === 'checkout' || stockWarmed) {
          // Stock + inventory already computed above — do not sync force-warm again (POS path).
          invalidateForDomainEvent(
            source === 'expiry' ? 'expiry_processed' : 'lot_updated'
          );
        } else {
          afterStockMovementCommit(
            source === 'expiry' ? 'expiry_processed' : 'lot_updated'
          );
        }
      } catch {
        /* facade optional */
      }
    }

    publishLegacyWindowEvent(STOCK_RECALCULATED_EVENT, {
      businessId: bid,
      source,
      showInventoryOverlay,
      entries: normalizedEntries,
      timestamp: Date.now()
    });

    // Checkout already applied lots via setLotsOnly — skip lotUpdated/inventory_updated
    // so POS does not remount the catalog on every Enregistrer.
    if (source !== 'checkout') {
      publishLotUpdated({ source, businessId: bid, timestamp: Date.now() });
    }

    return {
      success: batchResult?.success !== false,
      batchResult,
      stockWarmed
    };
  } catch (error) {
    console.error('[stockRecalculationOrchestrator] failed:', error?.message || error);
    return { success: false, error: error?.message || String(error), stockWarmed: false };
  } finally {
    coordinatedRecalcDepth = Math.max(0, coordinatedRecalcDepth - 1);
  }
}
